import type { ITravelerInfo } from '@pcln/horizon'
import { clampHotelOccupancy, type AiSearchRemovableFilter } from '@pcln/horizon-search-form'

import { addFormattedDays, normalizeFormDateString } from '@/shared-utils/date-helper'
import type { LOCATION_SEARCH_TYPE, TravelerState } from '@/types'

import { initialState } from '../SearchForm/utils'
import type { NlSearchFilter, NlSearchOccupancy, NlSearchResponse } from './aiSearch'
import { normalizeApiFilter } from './aiSearchFilterCodes'

export const PRICE_FILTER_ID = 'price'

/**
 * TravelerSelection reports every count as optional, but hotel form state requires all of
 * them — so chip edits are widened back to the form's shape at this one boundary instead of
 * the optionality leaking through to submit.
 */
export function toTravelerState(travelers: ITravelerInfo): TravelerState {
  return {
    adults: travelers.adults ?? initialState.travelers.adults,
    children: travelers.children ?? 0,
    childrenAges: travelers.childrenAges ?? {},
    rooms: travelers.rooms ?? initialState.travelers.rooms,
    lapInfants: travelers.lapInfants ?? 0,
  }
}

export type UserFieldOverrides = {
  city: boolean
  dates: boolean
  travelers: boolean
}

export type MappedSearchFilter = {
  id: string
  filterType: string
  filterValue: string
  filterLabel: string
}

export type AiSearchInterpretation = {
  city: LOCATION_SEARCH_TYPE | null
  startDate: string
  endDate: string
  travelers: TravelerState
  priceRange: { min: number | null; max: number | null }
  filters: MappedSearchFilter[]
  isPennyEligible: boolean
}

/** A response only overwrites what it resolved, so unresolved travelers stay `null`. */
export type AiSearchMappedResponse = Omit<AiSearchInterpretation, 'travelers'> & {
  travelers: TravelerState | null
}

function getFilterId(filter: Pick<NlSearchFilter, 'filterType' | 'filterValue'>) {
  return `filter:${filter.filterType}:${filter.filterValue}`
}

export function occupancyToTravelers(occupancy?: NlSearchOccupancy | null): TravelerState | null {
  if (!occupancy) return null

  // Ages come from the user, never the API — leave `childrenAges` empty so TravelerSelection's
  // built-in "please provide ages" validation fires when the user opens the chip.
  const counts = clampHotelOccupancy({
    rooms: occupancy.rooms ?? initialState.travelers.rooms,
    adults: occupancy.adults ?? initialState.travelers.adults,
    children: occupancy.children ?? 0,
  })
  return { ...counts, childrenAges: {}, lapInfants: 0 }
}

function trimDisplayLine(value?: string | null) {
  return value?.trim() || ''
}

function destinationDisplayName(destination: NonNullable<NlSearchResponse['destination']>) {
  const displayLine1 = trimDisplayLine(destination.displayLine1)
  const displayLine2 = trimDisplayLine(destination.displayLine2)
  if (displayLine1 && displayLine2) return `${displayLine1} - ${displayLine2}`
  return displayLine1 || displayLine2
}

/**
 * TravelerSelection echoes its current values on open, so an equality check is what
 * separates a real edit from a glance at the chip.
 */
export function areTravelersEqual(a: TravelerState, b: TravelerState) {
  if (a.adults !== b.adults || a.children !== b.children || a.rooms !== b.rooms) return false
  if (a.lapInfants !== b.lapInfants) return false

  const agesA = Object.values(a.childrenAges)
  const agesB = Object.values(b.childrenAges)

  return agesA.length === agesB.length && agesA.every((age, index) => age === agesB[index])
}

export function destinationToLocation(
  destination: NlSearchResponse['destination'],
): LOCATION_SEARCH_TYPE | null {
  if (!destination) return null

  const type = (destination.type || 'CITY') as LOCATION_SEARCH_TYPE['type']
  const cityID = destination.cityId ?? (type === 'CITY' ? destination.id : undefined)
  const id = type === 'CITY' ? (cityID ?? destination.id) : destination.id
  const itemName = destinationDisplayName(destination)

  if (!id || !itemName) return null

  const displayLine1 = trimDisplayLine(destination.displayLine1)
  const displayLine2 = trimDisplayLine(destination.displayLine2)

  return {
    type,
    id,
    cityID,
    itemName,
    displayName: itemName,
    displayLine1: displayLine1 || undefined,
    displayLine2: displayLine2 || undefined,
    countryCode: 'US',
  }
}

function nullablePrice(value?: number | null) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function formatPriceChipLabel(priceRange: { min: number | null; max: number | null }) {
  const { min, max } = priceRange
  if (min != null && max != null) return `$${min}–$${max}`
  if (max != null) return `Up to $${max}`
  if (min != null) return `From $${min}`
  return ''
}

export function mapNlSearchResponse(response: NlSearchResponse): AiSearchMappedResponse {
  const mappedFilters: MappedSearchFilter[] = []
  const seenFilterIds = new Set<string>()

  ;(response.filters ?? []).forEach((filter) => {
    const normalized = normalizeApiFilter(filter)
    if (!normalized) return

    const id = getFilterId(normalized)
    if (seenFilterIds.has(id)) return
    seenFilterIds.add(id)

    mappedFilters.push({
      id,
      filterType: normalized.filterType,
      filterValue: normalized.filterValue,
      filterLabel: filter.filterLabel?.trim() || filter.filterValue,
    })
  })

  return {
    city: destinationToLocation(response.destination),
    startDate: normalizeFormDateString(response.checkIn),
    endDate: normalizeFormDateString(response.checkOut),
    travelers: occupancyToTravelers(response.occupancy),
    priceRange: {
      min: nullablePrice(response.minPrice),
      max: nullablePrice(response.maxPrice),
    },
    filters: mappedFilters,
    isPennyEligible: Boolean(response.routeToPenny),
  }
}

function resolveMappedDates(
  mapped: Pick<AiSearchMappedResponse, 'startDate' | 'endDate'>,
  fallback: Pick<AiSearchInterpretation, 'startDate' | 'endDate'>,
) {
  if (mapped.startDate && mapped.endDate) {
    return { startDate: mapped.startDate, endDate: mapped.endDate }
  }
  if (mapped.startDate) {
    return { startDate: mapped.startDate, endDate: addFormattedDays(mapped.startDate, 1) }
  }
  if (mapped.endDate) {
    return { startDate: addFormattedDays(mapped.endDate, -1), endDate: mapped.endDate }
  }
  return { startDate: fallback.startDate, endDate: fallback.endDate }
}

export function applyMappedResponse(
  current: AiSearchInterpretation,
  mapped: AiSearchMappedResponse,
  overrides: UserFieldOverrides,
): AiSearchInterpretation {
  const dates = overrides.dates
    ? { startDate: current.startDate, endDate: current.endDate }
    : resolveMappedDates(mapped, current)

  return {
    city: overrides.city ? current.city : mapped.city,
    startDate: dates.startDate,
    endDate: dates.endDate,
    travelers: overrides.travelers ? current.travelers : (mapped.travelers ?? current.travelers),
    priceRange: mapped.priceRange,
    filters: mapped.filters,
    isPennyEligible: mapped.isPennyEligible,
  }
}

export function getVisibleRemovableFilters(
  interpretation: Pick<AiSearchInterpretation, 'priceRange' | 'filters'>,
  removedFilterIds: ReadonlySet<string>,
): AiSearchRemovableFilter[] {
  const chips: AiSearchRemovableFilter[] = []
  const priceLabel = formatPriceChipLabel(interpretation.priceRange)

  if (priceLabel && !removedFilterIds.has(PRICE_FILTER_ID)) {
    chips.push({ id: PRICE_FILTER_ID, label: priceLabel })
  }

  interpretation.filters.forEach((filter) => {
    if (removedFilterIds.has(filter.id)) return
    chips.push({ id: filter.id, label: filter.filterLabel })
  })

  return chips
}

export function getRetainedApiFilters(
  filters: MappedSearchFilter[],
  removedFilterIds: ReadonlySet<string>,
): Array<{ filterType: string; filterValue: string }> {
  return filters
    .filter((filter) => !removedFilterIds.has(filter.id))
    .map(({ filterType, filterValue }) => ({ filterType, filterValue }))
}

export function getRetainedPriceRange(
  priceRange: { min: number | null; max: number | null },
  removedFilterIds: ReadonlySet<string>,
): { min: number | null; max: number | null } {
  if (removedFilterIds.has(PRICE_FILTER_ID)) {
    return { min: null, max: null }
  }

  return priceRange
}

export function createInterpretation({
  startDate = '',
  endDate = '',
  travelers = initialState.travelers,
}: {
  startDate?: string
  endDate?: string
  travelers?: TravelerState
} = {}): AiSearchInterpretation {
  return {
    city: null,
    startDate,
    endDate,
    travelers,
    priceRange: { min: null, max: null },
    filters: [],
    isPennyEligible: false,
  }
}

export function interpretationFromMapped(
  mapped: AiSearchMappedResponse,
  fallback: AiSearchInterpretation,
): AiSearchInterpretation {
  const dates = resolveMappedDates(mapped, fallback)

  return {
    city: mapped.city,
    startDate: dates.startDate,
    endDate: dates.endDate,
    travelers: mapped.travelers ?? fallback.travelers,
    priceRange: mapped.priceRange,
    filters: mapped.filters,
    isPennyEligible: mapped.isPennyEligible,
  }
}
